import { useState, useEffect } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { Wordmark } from '../ui/Wordmark'
import {
  LayoutDashboard,
  Building2,
  CalendarDays,
  Users,
  CreditCard,
  BookOpen,
  Zap,
  BarChart2,
  ScrollText,
  ChevronRight,
  ClipboardList,
  Map,
  Timer,
  Tags,
  ShieldAlert,
} from 'lucide-react'
import {
  useAdminScrapedChurches,
  useAdminScrapedEvents,
  useEnrichBatchStatus,
  useAdminModeration,
  useAdminChurchApprovals,
} from '../../hooks/useAdmin'
import { useAuthStore } from '../../store/auth'

const LS_ENRICH_KEY = 'fellowship_enrich_batch_id'

function useActiveEnrichBatch() {
  const [batchId, setBatchId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(LS_ENRICH_KEY)
    } catch {
      return null
    }
  })

  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === LS_ENRICH_KEY) setBatchId(e.newValue)
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const status = useEnrichBatchStatus(batchId)

  useEffect(() => {
    if (status.data?.data.done) {
      setBatchId(null)
      try {
        localStorage.removeItem(LS_ENRICH_KEY)
      } catch {
        /* storage unavailable */
      }
    }
  }, [status.data?.data.done])

  if (!batchId) return null
  return status.data?.data ?? null
}

function NavBadge({ count }: { count: number }) {
  if (count === 0) return null
  return (
    <span
      style={{
        fontSize: 10,
        padding: '1px 6px',
        borderRadius: 999,
        background: 'rgba(124,255,178,.18)',
        color: '#7CFFB2',
        fontWeight: 700,
        fontFamily: 'ui-monospace,"SF Mono",Menlo,monospace',
      }}
    >
      {count}
    </span>
  )
}

function ChurchBadge() {
  const { data } = useAdminScrapedChurches()
  return <NavBadge count={data?.data.length ?? 0} />
}

function EventBadge() {
  const { data } = useAdminScrapedEvents()
  return <NavBadge count={data?.data.length ?? 0} />
}

function ModerationBadge() {
  const { data } = useAdminModeration({ page: 1, status: 'pending' })
  return <NavBadge count={data?.meta.total ?? 0} />
}

function ChurchApprovalBadge() {
  const { data } = useAdminChurchApprovals()
  return <NavBadge count={data?.data.length ?? 0} />
}

type NavItem =
  | { kind: 'link'; to: string; icon: React.ElementType; label: string; badge?: React.ReactNode }
  | { kind: 'stub'; icon: React.ElementType; label: string }
  | { kind: 'divider' }

const NAV_ITEMS: NavItem[] = [
  {
    kind: 'link',
    to: '/admin/churches',
    icon: Building2,
    label: 'Churches',
    badge: <ChurchBadge />,
  },
  { kind: 'link', to: '/admin/events', icon: CalendarDays, label: 'Events', badge: <EventBadge /> },
  { kind: 'link', to: '/admin/event-tags', icon: Tags, label: 'Event tags' },
  { kind: 'link', to: '/admin/users', icon: Users, label: 'Users' },
  { kind: 'divider' },
  { kind: 'link', to: '/admin/scrape', icon: Zap, label: 'Scraper' },
  { kind: 'link', to: '/admin/coverage', icon: Map, label: 'Coverage map' },
  { kind: 'link', to: '/admin/jobs', icon: Timer, label: 'Background jobs' },
  { kind: 'stub', icon: LayoutDashboard, label: 'Sources' },
  { kind: 'stub', icon: CreditCard, label: 'Subscriptions' },
  { kind: 'divider' },
  { kind: 'link', to: '/admin/waitlist', icon: BookOpen, label: 'Waitlist' },
  {
    kind: 'link',
    to: '/admin/church-approvals',
    icon: ClipboardList,
    label: 'Church approvals',
    badge: <ChurchApprovalBadge />,
  },
  { kind: 'link', to: '/admin/church-requests', icon: ClipboardList, label: 'Church requests' },
  { kind: 'link', to: '/admin/claim-requests', icon: ClipboardList, label: 'Claim requests' },
  {
    kind: 'link',
    to: '/admin/moderation',
    icon: ShieldAlert,
    label: 'Moderation',
    badge: <ModerationBadge />,
  },
  { kind: 'stub', icon: BarChart2, label: 'Reports' },
  { kind: 'link', to: '/admin/audit-log', icon: ScrollText, label: 'Audit log' },
]

const stubStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 10px',
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 400,
  color: 'rgba(255,255,255,0.25)',
  marginBottom: 1,
  cursor: 'default',
  userSelect: 'none',
}

export function SuperAdminLayout() {
  const { user } = useAuthStore()
  const enrichBatch = useActiveEnrichBatch()
  const initials = user?.displayName
    ? user.displayName
        .split(' ')
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : '??'

  return (
    <div className="b-system" style={{ minHeight: '100vh', display: 'flex' }}>
      {/* Dark sidebar */}
      <aside
        style={{
          width: 224,
          flexShrink: 0,
          background: '#0a0c1f',
          display: 'flex',
          flexDirection: 'column',
          padding: '20px 12px 24px',
          position: 'sticky',
          top: 0,
          height: '100vh',
          overflowY: 'auto',
        }}
      >
        {/* Logo + platform badge */}
        <div style={{ padding: '4px 8px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Wordmark size={16} variant="light" />
          <span
            style={{
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: '0.07em',
              textTransform: 'uppercase',
              color: '#0a0c1f',
              background: '#7CFFB2',
              padding: '2px 6px',
              borderRadius: 4,
            }}
          >
            PLATFORM
          </span>
        </div>

        {/* User card */}
        <div
          style={{
            padding: '9px 10px',
            borderRadius: 10,
            background: 'rgba(255,255,255,.06)',
            border: '1px solid rgba(255,255,255,.08)',
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            marginBottom: 14,
          }}
        >
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 6,
              background: 'linear-gradient(135deg, #3B4CCA, #7C5CD3)',
              color: '#fff',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              flexShrink: 0,
            }}
          >
            {initials}
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 12.5,
                fontWeight: 600,
                color: '#fff',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user?.displayName ?? 'Admin'}
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,.45)', marginTop: 1 }}>
              Super-admin
            </div>
          </div>
          <ChevronRight
            size={13}
            style={{ marginLeft: 'auto', color: 'rgba(255,255,255,.2)', flexShrink: 0 }}
          />
        </div>

        {/* Nav */}
        <nav style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {NAV_ITEMS.map((item, i) => {
            if (item.kind === 'divider') {
              return (
                <div
                  key={i}
                  style={{ height: 1, background: 'rgba(255,255,255,.06)', margin: '6px 0' }}
                />
              )
            }
            if (item.kind === 'stub') {
              const Icon = item.icon
              return (
                <div key={item.label} style={stubStyle}>
                  <Icon size={15} strokeWidth={1.6} />
                  <span style={{ flex: 1 }}>{item.label}</span>
                  <span
                    style={{
                      fontSize: 9,
                      color: 'rgba(255,255,255,.2)',
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                    }}
                  >
                    soon
                  </span>
                </div>
              )
            }
            const Icon = item.icon
            return (
              <NavLink
                key={item.to}
                to={item.to}
                style={({ isActive }) => ({
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '8px 10px',
                  borderRadius: 8,
                  textDecoration: 'none',
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? '#ffffff' : 'rgba(255,255,255,0.55)',
                  background: isActive ? 'rgba(255,255,255,.08)' : 'transparent',
                })}
              >
                <Icon size={15} strokeWidth={1.6} />
                <span style={{ flex: 1 }}>{item.label}</span>
                {item.badge}
              </NavLink>
            )
          })}
        </nav>

        <div style={{ flex: 1 }} />

        {/* Enrich job status — shown only when a batch is running */}
        {enrichBatch && (
          <div
            style={{
              padding: '10px 12px',
              borderRadius: 8,
              background: 'rgba(255,255,255,.04)',
              borderLeft: '3px solid #7C9CFF',
              marginBottom: 8,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: '#7C9CFF',
                  display: 'inline-block',
                  animation: 'pulse-blue 2s ease-in-out infinite',
                }}
              />
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'rgba(255,255,255,.75)',
                  letterSpacing: '0.04em',
                }}
              >
                ENRICHING
              </span>
            </div>
            <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,.35)', marginTop: 4 }}>
              {enrichBatch.completed + enrichBatch.failed} / {enrichBatch.total} churches done
            </div>
          </div>
        )}

        {/* Scraper live status */}
        <div
          style={{
            padding: '10px 12px',
            borderRadius: 8,
            background: 'rgba(255,255,255,.04)',
            borderLeft: '3px solid #7CFFB2',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: '#7CFFB2',
                display: 'inline-block',
                animation: 'pulse-green 2s ease-in-out infinite',
              }}
            />
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'rgba(255,255,255,.75)',
                letterSpacing: '0.04em',
              }}
            >
              SCRAPER · LIVE
            </span>
          </div>
          <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,.35)', marginTop: 4 }}>
            Last run 3 min ago
          </div>
        </div>

        <div style={{ paddingTop: 16 }}>
          <NavLink
            to="/"
            style={{ fontSize: 12, color: 'rgba(255,255,255,.35)', textDecoration: 'none' }}
          >
            ← Back to app
          </NavLink>
        </div>
      </aside>

      {/* Main content */}
      <main style={{ flex: 1, background: 'var(--bg)', minWidth: 0 }}>
        <div style={{ margin: '0 auto', padding: '32px 40px 60px' }}>
          <Outlet />
        </div>
      </main>

      <style>{`
        @keyframes pulse-green {
          0%, 100% { box-shadow: 0 0 0 0 rgba(124,255,178,.5); }
          50% { box-shadow: 0 0 0 5px rgba(124,255,178,0); }
        }
        @keyframes pulse-blue {
          0%, 100% { box-shadow: 0 0 0 0 rgba(124,156,255,.5); }
          50% { box-shadow: 0 0 0 5px rgba(124,156,255,0); }
        }
      `}</style>
    </div>
  )
}
